import { computed, inject, Injectable, InjectionToken, signal } from '@angular/core';
import { OrganizationsService, OrganizationSummary } from '@wheere/api-user';
import { lastValueFrom } from 'rxjs';

/**
 * localStorage key holding the picked org id. Per-app so the two admin apps don't fight over one
 * scope when both are open in the same browser — override it in each app's `app.config.ts`.
 */
export const ADMIN_SCOPE_STORAGE_KEY = new InjectionToken<string>('Admin scope storage key', {
  providedIn: 'root',
  factory: () => 'wheere.adminScope.orgId.v1',
});

/**
 * Single source of truth for the "admin mode" organization filter. PLATFORM_ADMIN /
 * PLATFORM_SUPPORT users pick an org once in the navbar; every list page (sites / devices /
 * gateways) reads this signal and scopes its backend query accordingly. {@code null} means
 * "all orgs" (the cross-org overview).
 *
 * <p>ORG_* users don't interact with this service — their JWT carries the canonical org id and
 * the backend filters server-side.
 *
 * <p>Persistence: the picked id is written to {@code localStorage} (see
 * {@link ADMIN_SCOPE_STORAGE_KEY}) so a refresh keeps the operator on the same scope.
 *
 * <p>Why a service rather than a route param: the original design used {@code ?orgId=…} on each
 * list page, but that meant clicking from devices to gateways dropped the filter (different URL =
 * different param). A shared service is the correct shape for "global filter persisted across nav".
 */
@Injectable({ providedIn: 'root' })
export class AdminScopeService {
  #storageKey = inject(ADMIN_SCOPE_STORAGE_KEY);

  /** Currently-selected org id; {@code null} means cross-org. Listeners observe the signal
   *  directly — no need for an emit/subscribe boilerplate. */
  readonly selectedOrgId = signal<string | null>(this.loadFromStorage());

  /** Cached org catalogue (lazy-loaded, single fetch per session) — surfaces the org name for
   * badges / lookup tables that only have the id. Empty until {@link loadOrgs} resolves. */
  readonly orgs = signal<OrganizationSummary[]>([]);

  /** {@code id → name} index, derived once {@link orgs} loads. */
  readonly orgNameById = computed(() => {
    const map = new Map<string, string>();
    for (const o of this.orgs()) map.set(o.id ?? '', o.name ?? '');
    return map;
  });

  #orgService = inject(OrganizationsService);
  #loadingOrgs = false;

  /** Idempotent — first call kicks off the fetch, subsequent calls return immediately. The cache
   * is per-session; a refresh re-fetches. */
  async loadOrgs(): Promise<void> {
    if (this.orgs().length > 0 || this.#loadingOrgs) return;
    this.#loadingOrgs = true;
    try {
      const resp = await lastValueFrom(this.#orgService.listOrganizations(0, 200));
      this.orgs.set(resp.content ?? []);
    } catch {
      this.orgs.set([]);
    } finally {
      this.#loadingOrgs = false;
    }
  }

  /** Convenience helper for templates: returns the org name for the given id, or {@code null} if
   * the catalogue isn't loaded yet / id unknown. */
  orgName(orgId: string | null | undefined): string | null {
    if (!orgId) return null;
    return this.orgNameById().get(orgId) ?? null;
  }

  /**
   * Set the active scope. Empty string is normalised to {@code null} so URL-driven flows
   * ({@code ?orgId=}) that pass an empty value reset to "all".
   */
  setOrg(orgId: string | null | undefined): void {
    const next = orgId && orgId.length > 0 ? orgId : null;
    if (this.selectedOrgId() === next) return;
    this.selectedOrgId.set(next);
    this.saveToStorage(next);
  }

  /** Convenience clear — same effect as {@code setOrg(null)}. */
  clear(): void {
    this.setOrg(null);
  }

  // ── Storage helpers (localStorage may be unavailable in private mode / SSR; wrap every
  // access). ───────────────────────────────────────────────────────────────────────────────

  private loadFromStorage(): string | null {
    try {
      return localStorage.getItem(this.#storageKey);
    } catch {
      return null;
    }
  }

  private saveToStorage(orgId: string | null): void {
    try {
      if (orgId === null) {
        localStorage.removeItem(this.#storageKey);
      } else {
        localStorage.setItem(this.#storageKey, orgId);
      }
    } catch {
      // Ignore — non-fatal. The signal value still lives for the session.
    }
  }
}
